import type { MarketPair } from "@cede/types";
import { API_ENDPOINT, Logger, isDexId } from "@cede/utils";
import { useApi } from "@/hooks/core/useApi";
import { useQuery } from "@tanstack/react-query";

/**
 * Hook to fetch market pair data including precision
 * Uses public endpoint, no authentication required
 * Note: Only works for CEX exchanges - DEXs don't have traditional market pairs
 */
export const useMarketPairs = (exchangeId?: string) => {
  const apiHelper = useApi();

  // Skip for DEX exchanges
  const isFetchable = exchangeId ? !isDexId(exchangeId) : false;

  const { data: markets, isLoading } = useQuery({
    queryKey: ["marketPairs", exchangeId],
    queryFn: async () => {
      if (!exchangeId) return null;

      try {
        // Use public endpoint without authentication
        const markets: MarketPair[] = await apiHelper.getOrThrow(API_ENDPOINT.HUB_TRADE_MARKET_PAIRS, {
          queryParams: { exchangeId },
        });
        return markets;
      } catch (error) {
        Logger.error("Error fetching market pair:", error);
        return null;
      }
    },
    enabled: isFetchable,
    staleTime: 5 * 60 * 1000, // 5 minutes - market precision doesn't change often
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  // Normalize undefined to null for consistent behavior when query is disabled
  return { markets: markets ?? null, isLoading };
};
